import _ from 'underscore';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { anime } from 'react-anime';
import cx from 'classnames';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { useIsMobile } from 'hooks/useIsMobile';
import PodcastsList from 'components/PodcastsList';
import StyledContentBlock from './components/StyledContentBlock';
import styles from './styles.module.scss';

const {
	publicRuntimeConfig: {
		features: { headerHeight }
	}
} = getConfig();

const offsetTopGutterDesktop = 24;
const offsetTopGutterMobile = 8;

const scrollTrigger = (refs, value, active, isMobile) => {
	const getIndex = (title) => _.keys(refs.current).indexOf(title);

	const valueIndex = getIndex(value);
	const activeIndex = getIndex(active);

	const pviPanelHeight =
		parseInt(
			getComputedStyle(document.documentElement).getPropertyValue(
				'--pvi-panel-height'
			),
			10
		) || 0;

	const headerOffsetWithGutter =
		headerHeight +
		(isMobile ? offsetTopGutterMobile : offsetTopGutterDesktop) +
		pviPanelHeight;

	const distanceToTop =
		activeIndex > valueIndex || activeIndex === -1
			? window.scrollY +
			  refs.current[value].getBoundingClientRect().top -
			  headerOffsetWithGutter
			: window.scrollY +
			  refs.current[value].getBoundingClientRect().top -
			  refs.current[active]?.children[1]?.scrollHeight -
			  headerOffsetWithGutter;

	window.scrollTo({
		top: distanceToTop,
		left: 0,
		behavior: 'smooth'
	});
};

const StyledContent = ({
	blocks,
	defaultBlockHeight,
	minOffset,
	firstParagraph,
	children,
	accordion,
	className,
	getHeaderRefs,
	podcasts,
	afterAccordionNode,
	ariaLabel
}) => {
	const textBlockRef = useRef(null);
	const wrapperRef = useRef(null);
	const spoilerRefs = useRef({});
	const { asPath } = useRouter();

	const getSpoilerRefs = (value) => (el) => {
		spoilerRefs.current[value] = el;
	};

	const isMobile = useIsMobile();

	const [isOpen, setIsOpen] = useState(false);
	const [opened, setOpened] = useState(false);
	const [isAccordionMode, setIsAccordionMode] = useState(false);
	const [height, setHeight] = useState(0);
	const [activeSpoiler, setActiveSpoiler] = useState(null);

	useEffect(() => {
		if (isOpen || opened) {
			setOpened(false);
			setIsOpen(false);
		}
	}, [asPath]);

	const handleSpoilerClick = (value) => {
		if (activeSpoiler === value) {
			return setActiveSpoiler(null);
		}

		scrollTrigger(spoilerRefs, value, activeSpoiler, isMobile);
		return setActiveSpoiler(value);
	};

	const getSpoilerContentHeight = (value) =>
		activeSpoiler === value
			? spoilerRefs.current[value]?.children[1]?.scrollHeight
			: 0;

	const changeBlockHeight = useCallback(
		(newHeight) => {
			if (textBlockRef.current && accordion) {
				const blockHeight = textBlockRef.current.offsetHeight;
				const newIsAccordionMode = blockHeight > defaultBlockHeight + minOffset;

				if (newHeight === undefined || !newIsAccordionMode) {
					setIsAccordionMode(newIsAccordionMode);
				}

				if (!newIsAccordionMode && newHeight === undefined) {
					setIsOpen(newIsAccordionMode);
					setOpened(newIsAccordionMode);
				}

				const newHeightValue =
					newHeight || (isOpen ? blockHeight : defaultBlockHeight);

				setHeight(newIsAccordionMode ? newHeightValue : 0);
			}
		},
		[textBlockRef.current, isOpen, accordion]
	);

	const onChangeInnerBlockContent = useCallback(
		(diff) => {
			if (textBlockRef.current && diff && accordion) {
				setHeight((prevHeight) => {
					const blockHeight = textBlockRef.current.offsetHeight;
					const newHeight = (prevHeight || blockHeight) + diff;

					changeBlockHeight(newHeight);

					return newHeight;
				});
			}
		},
		[changeBlockHeight, accordion]
	);

	useEffect(() => {
		changeBlockHeight();
	}, []);

	useEffect(() => {
		let width = window.outerWidth;

		const onResize = _.debounce(() => {
			if (width !== window.outerWidth) {
				width = window.outerWidth;

				changeBlockHeight();
			}
		}, 10);

		window.addEventListener('resize', onResize);

		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [blocks, changeBlockHeight, accordion]);

	const expandBlock = useCallback(() => {
		setIsOpen((prevIsOpen) => {
			const newIsOpen = !prevIsOpen;

			if (isAccordionMode) {
				const scrollElement =
					window.document.scrollingElement ||
					window.document.documentElement ||
					window.document.body;
				const boundingTextBlock = textBlockRef.current.getBoundingClientRect();
				const pageScrollTop =
					window.pageYOffset || document.documentElement.scrollTop;

				anime({
					targets: wrapperRef.current,
					height: 'auto',
					duration: 150,
					easing: 'easeInOutSine',
					[!newIsOpen ? 'begin' : 'complete']: () => {
						setOpened(newIsOpen);
					}
				});

				anime({
					targets: scrollElement,
					scrollTop: !newIsOpen
						? pageScrollTop - boundingTextBlock.height + defaultBlockHeight
						: pageScrollTop,
					duration: 150,
					easing: 'easeInOutSine',
					complete: () => {
						const currentMaxHeight = parseInt(
							wrapperRef.current.style.maxHeight,
							10
						);

						setHeight(currentMaxHeight);
					}
				});
			}

			return newIsOpen;
		});
	}, [isAccordionMode]);

	const styledContent = cx(styles.content, className, {
		[styles.accordion]: isAccordionMode,
		[styles.opened]: opened && isAccordionMode
	});
	return (
		<div
			className={styledContent}
			aria-label={ariaLabel}
			// eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
			tabIndex={ariaLabel ? 0 : -1}
		>
			<div
				className={styles.wrapper}
				ref={wrapperRef}
				style={{ minHeight: height ? `${height}px` : 'none' }}
			>
				<div className={styles.body} ref={textBlockRef}>
					{!_.isEmpty(podcasts) && <PodcastsList {...{ podcasts }} />}
					{_(blocks).map((block, index) => (
						<StyledContentBlock
							// eslint-disable-next-line react/no-array-index-key
							key={`${block.type}-${index}`}
							blockKey={`${block.type}-${index}`}
							onChangeContent={onChangeInnerBlockContent}
							{...block}
							firstParagraph={firstParagraph ?? block.firstParagraph}
							getSpoilerRefs={getSpoilerRefs}
							getHeaderRefs={getHeaderRefs}
							onSpoilerClick={handleSpoilerClick}
							getSpoilerContentHeight={getSpoilerContentHeight}
							activeSpoiler={activeSpoiler}
						/>
					))}
					{children}
				</div>
			</div>
			{accordion && isAccordionMode && (
				<button
					className={styles.button}
					type="button"
					onClick={expandBlock}
					aria-hidden
				>
					{isOpen ? 'Свернуть' : 'Развернуть'}
				</button>
			)}
			{!!afterAccordionNode && (
				<div className={styles.body}>{afterAccordionNode}</div>
			)}
		</div>
	);
};

StyledContent.propTypes = {
	blocks: PropTypes.arrayOf(
		PropTypes.shape({ type: PropTypes.string.isRequired }).isRequired
	).isRequired,
	defaultBlockHeight: PropTypes.number,
	minOffset: PropTypes.number,
	firstParagraph: PropTypes.bool,
	children: PropTypes.node,
	className: PropTypes.string,
	accordion: PropTypes.bool,
	getHeaderRefs: PropTypes.func,
	podcasts: PropTypes.array,
	afterAccordionNode: PropTypes.node,
	ariaLabel: PropTypes.string
};

StyledContent.defaultProps = {
	defaultBlockHeight: 525,
	minOffset: 200,
	accordion: false
};

export default StyledContent;
